import hoistNonReactStatics from 'hoist-non-react-statics';
import React, { useEffect, useRef, useState } from 'react';
import { cssVariables } from 'styles';

export enum ViewportWidthBreakpoint {
  Narrow = 0,
  Medium = 1,
  Wide = 2,
}

type MediaQueryChangeEventListener = (event: MediaQueryListEvent) => unknown;

const mediaQuerySupportsEventTargetMethods =
  typeof window !== 'undefined' &&
  Object.getPrototypeOf(window.matchMedia('')) instanceof EventTarget;

/**
 * Appends a `change` event listener to a `MediaQueryList` instance.
 * - Delegates to `addEventListener` in modern browsers.
 * - Delegates to `addListener` in Safari < 14.
 */
export const addMediaQueryListener = mediaQuerySupportsEventTargetMethods
  ? (mediaQuery: MediaQueryList, listener: MediaQueryChangeEventListener) =>
      mediaQuery.addEventListener('change', listener)
  : (mediaQuery: MediaQueryList, listener: MediaQueryChangeEventListener) =>
      mediaQuery.addListener(listener);

/**
 * Removes a `change` event listener from a `MediaQueryList` instance.
 * - Delegates to `removeEventListener` in modern browsers.
 * - Delegates to `removeListener` in Safari < 14.
 */
export const removeMediaQueryListener = mediaQuerySupportsEventTargetMethods
  ? (mediaQuery: MediaQueryList, listener: MediaQueryChangeEventListener) =>
      mediaQuery.removeEventListener('change', listener)
  : (mediaQuery: MediaQueryList, listener: MediaQueryChangeEventListener) =>
      mediaQuery.removeListener(listener);

/**
 * Tracks whether the given media query matches.
 */
export const useMediaQuery = (query: string) => {
  const mediaQuery = useRef(window.matchMedia(query));
  const [matches, setMatches] = useState(mediaQuery.current.matches);

  useEffect(() => {
    const handleChange: MediaQueryChangeEventListener = e => {
      if (matches !== e.matches) {
        setMatches(e.matches);
      }
    };

    addMediaQueryListener(mediaQuery.current, handleChange);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    return () => removeMediaQueryListener(mediaQuery.current, handleChange);
  }, [matches]);

  return matches;
};

/**
 * Tracks the current viewport width breakpoint.
 */
export const useViewportWidthBreakpoint = () => {
  const matchesMediumBreakpoint = useMediaQuery(
    `screen and (min-width: ${cssVariables.reportMediumBreakpoint}px)`
  );
  const matchesWideBreakpoint = useMediaQuery(
    `screen and (min-width: ${cssVariables.reportWideBreakpoint}px)`
  );

  if (matchesWideBreakpoint) {
    return ViewportWidthBreakpoint.Wide;
  } else if (matchesMediumBreakpoint) {
    return ViewportWidthBreakpoint.Medium;
  } else {
    return ViewportWidthBreakpoint.Narrow;
  }
};

export const useViewportWidthAtLeast = (px: number) => {
  const matches = useMediaQuery(`screen and (min-width: ${px}px)`);

  return matches;
};

/**
 * Mobile breakpoint constant (matches $breakPointMedium in variables.scss)
 */
export const MOBILE_BREAKPOINT = 768;

/**
 * Returns true when viewport width is below the mobile breakpoint (768px).
 * Use this to conditionally render mobile-optimized layouts.
 */
export const useIsMobile = () => {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
};

/**
 * Used for the injected props when applying `withViewportWidthBreakpoint`.
 */
export interface ViewportWidthBreakpointProps {
  currentWidthBreakpoint: ViewportWidthBreakpoint;
}

/**
 * Higher-order component that injects the current viewport width breakpoint.
 */
export const withViewportWidthBreakpoint = <P extends ViewportWidthBreakpointProps>(
  Component: React.ComponentType<P>
) => {
  type PropsWithoutInjected = Omit<P, keyof ViewportWidthBreakpointProps>;
  const Wrapper = React.forwardRef((props: PropsWithoutInjected, ref) => {
    const currentWidthBreakpoint = useViewportWidthBreakpoint();

    return (
      // The cast/spread order is needed to work around this bug:
      // https://github.com/Microsoft/TypeScript/issues/28938
      <Component ref={ref} {...(props as P)} currentWidthBreakpoint={currentWidthBreakpoint} />
    );
  });
  Wrapper.displayName = `withViewportWidthBreakpoint(${Component.displayName || Component.name})`;

  const WrapperWithAttributes: typeof Wrapper & { WrappedComponent?: React.ComponentType<P> } =
    Wrapper;
  WrapperWithAttributes.WrappedComponent = Component;

  return hoistNonReactStatics(WrapperWithAttributes, Component);
};
